import React from 'react'
import * as Highcharts from 'highcharts'
import { theme as deprecatedTheme } from '../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { I18nConsumer } from '../i18n-provider'
import { Chart } from '../../legacy-design-system/charts'
import { IntlShape } from 'react-intl'

export interface RouteCoveragePieChartProps
  extends RouteCoveragePieChartValues {
  readonly size: number
}

interface RouteCoveragePieChartValues {
  readonly routesDiscovered: number
  readonly routesVulnerable: number
  readonly routesExercised: number
}

const ROUTES_NOT_EXERCISED = 'ROUTES_NOT_EXERCISED'
const ROUTES_VULNERABLE = 'VULNERABLE'
const ROUTES_EXERCISED = 'EXERCISED'

export function RouteCoveragePieChart(props: RouteCoveragePieChartProps) {
  const { routesDiscovered, routesExercised, routesVulnerable, size } = props
  const routesNotExercised = routesDiscovered - routesExercised
  const routesExercisedNotVulnerable = routesExercised - routesVulnerable

  const pieChartSize = size - 20 //  chart needs to be smaller than container size so it has margins
  const titleTextSize = Math.max(20, Math.round(size / 5))
  const titlePercentSize = Math.max(10, Math.round(size / 10))

  const percentTitle = calcTitlePercent(props)

  return (
    <I18nConsumer>
      {({ intl }) => (
        <Chart
          testId="route-coverage-pie-chart"
          options={{
            series: [
              {
                type: 'pie',
                innerSize: '80%',
                fillColor: theme.color.neutral40,
                data: [
                  [ROUTES_NOT_EXERCISED, routesNotExercised],
                  [ROUTES_EXERCISED, routesExercisedNotVulnerable],
                  [ROUTES_VULNERABLE, routesVulnerable]
                ]
              }
            ],
            tooltip: {
              backgroundColor: theme.color.neutral90,
              borderColor: theme.color.neutral90,
              style: {
                color: theme.color.white
              },
              padding: 15,
              useHTML: true,
              headerFormat: undefined,
              pointFormatter() {
                //  see https://api.highcharts.com/highcharts/tooltip.pointFormatter
                //  function doesn't pass the point object but has point scoped as `this`
                return formatTooltip(intl, this as Highcharts.Point, {
                  routesDiscovered,
                  routesNotExercised,
                  routesExercised,
                  routesExercisedNotVulnerable,
                  routesVulnerable
                })
              },
              outside: true
            },
            title: {
              text:
                `<span style="font-size: ${titleTextSize}px; font-family: ${deprecatedTheme.fontFamilies.secondary}; font-weight: bold; color: #808080;">${percentTitle}</span>` +
                `<span style="font-size: ${titlePercentSize}px; font-family: ${deprecatedTheme.fontFamilies.secondary}; color: #BFBFBF;">%</span>`,
              align: 'center',
              verticalAlign: 'middle',
              y: titleTextSize //  this is so the title is aligned properly in the center
            },
            chart: {
              height: size,
              width: size,
              margin: 50
            },
            plotOptions: {
              pie: {
                dataLabels: {
                  enabled: false
                },
                borderWidth: 5,
                colors: [
                  theme.color.neutral40,
                  theme.color.turquoise70,
                  theme.color.red50
                ],
                size: pieChartSize,
                animation: false
              }
            }
          }}
        />
      )}
    </I18nConsumer>
  )
}

const tooltipStyle = `font-size: 12px; font-family: ${deprecatedTheme.fontFamilies.secondary};`
function formatTooltip(
  intl: IntlShape,
  point: Highcharts.Point,
  {
    routesDiscovered,
    routesNotExercised,
    routesExercised,
    routesExercisedNotVulnerable,
    routesVulnerable
  }: {
    readonly routesDiscovered: number
    readonly routesNotExercised: number
    readonly routesExercised: number
    readonly routesExercisedNotVulnerable: number
    readonly routesVulnerable: number
  }
) {
  const { name } = point
  switch (name) {
    case ROUTES_NOT_EXERCISED:
      return `<span style="${tooltipStyle}">
        ${getTooltipMessage(
          intl,
          'detail_applicationCoverage_routesNotExercised',
          routesNotExercised
        )}
      </span>`
    case ROUTES_EXERCISED:
      return `<span style="${tooltipStyle}">
        <p>
          ${getTooltipMessage(
            intl,
            'detail_applicationCoverage_routesAreExercised',
            routesExercised
          )}
        </p>
        <p>
          ${getTooltipMessage(
            intl,
            'detail_applicationCoverage_routesAreExercisedNotVulnerable',
            routesExercisedNotVulnerable
          )}
        </p>
          ${
            /* do not have a paragraph in last element to avoid weird margin bottom padding */
            getTooltipMessage(
              intl,
              'detail_applicationCoverage_routesPercentExercised',
              roundTooltipPercentage(routesExercised, routesDiscovered)
            )
          }
        </span>`
    case ROUTES_VULNERABLE:
      return `<span style="${tooltipStyle}">
        <p>
          ${getTooltipMessage(
            intl,
            'detail_applicationCoverage_routesAreExercisedAreVulnerable',
            routesVulnerable
          )}
        </p>
          ${
            /* do not have a paragraph in last element to avoid weird margin bottom padding */
            getTooltipMessage(
              intl,
              'detail_applicationCoverage_routesPercentVulnerable',
              roundTooltipPercentage(routesVulnerable, routesExercised)
            )
          }
      </span>`
    default:
      throw new Error(
        'Unsupported route coverage category encountered: ' + point.name
      )
  }
}

function getTooltipMessage(intl: IntlShape, messageId: string, count: number) {
  return intl.formatMessage(
    {
      id: messageId
    },
    {
      count
    }
  )
}

export function calcTitlePercent({
  routesDiscovered,
  routesExercised
}: {
  readonly routesDiscovered: number
  readonly routesExercised: number
}): number {
  return routesDiscovered === 0
    ? 0
    : Math.round((routesExercised / routesDiscovered) * 100)
}

export function roundTooltipPercentage(
  numerator: number,
  denominator: number
): number {
  return denominator === 0
    ? 0
    : Math.round((numerator / denominator) * 1000) / 10
}
